import styled, { keyframes } from 'styled-components';
import { Container, MessagesList } from '../../shared.styled';

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

export const ContainerWithRelative = styled(Container)`
  position: relative;
`;

/** Full-width section for conversation header + search; sits above Content (no 750px constraint). */
export const HeaderSectionFullWidth = styled.div`
  width: 100%;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  pointer-events: none;
`;

export const LoadingOverlayContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 24px 32px;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  animation: ${fadeIn} 0.2s ease-in-out;
`;

export const MessagesListWithLoading = styled(MessagesList)`
  opacity: ${(props) => (props.$isLoading ? 0.4 : 1)};
  transition: opacity 0.3s ease-in-out;
  filter: ${(props) => (props.$isLoading ? 'blur(1px)' : 'none')};
  pointer-events: ${(props) => (props.$isLoading ? 'none' : 'auto')};
  overscroll-behavior: none;
  overscroll-behavior-y: none;

  /* Prevent bounce on the scrollable inner element (Scrollbar from @ann/web-components) */
  & > * {
    overscroll-behavior: none;
    overscroll-behavior-y: none;
  }
`;

/** Wrapper for attachment chips below user message text (read-only). */
export const MessageAttachmentsList = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  gap: ${(props) => props.theme.spacing?.sm || '8px'};
  margin-top: ${(props) => props.theme.spacing?.sm || '8px'};
  width: 100%;
`;

export const OpenDocumentCardWrap = styled.div`
  margin-top: ${(props) => props.theme.spacing?.sm || '8px'};
  width: 100%;
`;

export const ReasoningBarWrap = styled.div`
  padding-top: 8px;
  padding-bottom: 8px;
  width: 100%;
`;

export const SearchWrapper = styled.div`
  display: none;
  flex-direction: row;
  align-items: center;
  gap: 16px;
  width: 750px;
  max-width: 100%;
  padding: 0 12px;
  margin: 0 auto;

  ${(props) =>
    !props.$hidden &&
    `
    display: flex;
  `}
`;

export const SearchInputWrapper = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  height: 40px;
  padding: 4px 16px;
  align-items: center;
  gap: 4px;
  background: ${(props) => props.theme.colors?.white ?? '#FFFFFF'};
  border: 1px solid ${(props) => props.theme.colors?.border ?? '#D1D5DB'};
  border-radius: 8px;
  box-sizing: border-box;
  transition: border-color 0.2s ease;

  &:focus-within {
    border-color: ${(props) => props.theme.colors?.primary ?? '#2173ff'};
  }
`;

export const SearchIconLeft = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${(props) => props.theme.colors?.textSecondary ?? '#6B7280'};
`;

export const SearchInputWithClearWrap = styled.div`
  flex: 1;
  min-width: 0;
  position: relative;
  display: flex;
  align-items: center;
  height: 100%;
`;

export const SearchInputField = styled.input`
  width: 100%;
  height: 100%;
  border: none;
  background: transparent;
  padding: 0 28px 0 0;
  font-size: 14px;
  line-height: 20px;
  color: ${(props) => props.theme.colors?.text ?? '#2a2a2a'};
  box-sizing: border-box;

  &::placeholder {
    color: ${(props) => props.theme.colors?.textSecondary ?? '#9CA3AF'};
  }

  &:focus {
    outline: none;
  }
`;

export const SearchClearButton = styled.button`
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  background: transparent;
  border-radius: 50%;
  color: ${(props) => props.theme.colors?.textSecondary ?? '#6B7280'};
  cursor: pointer;
  transition: color 0.15s ease, background-color 0.15s ease;

  &:hover {
    color: ${(props) => props.theme.colors?.text ?? '#2a2a2a'};
    background-color: ${(props) => props.theme.colors?.border ?? '#E5E7EB'};
  }

  &:focus {
    outline: none;
  }
`;

export const SearchWrapperCount = styled.div`
  display: flex;
  align-items: center;
  white-space: nowrap;
  flex-shrink: 0;
`;

export const SearchWrapperIconsGroup = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
`;

export const SearchWrapperIconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border: none;
  background: transparent;
  border-radius: 8px;
  cursor: pointer;
  color: ${(props) => props.theme.colors?.textSecondary ?? '#6B7280'};
  transition: background-color 0.15s ease;

  &:hover {
    background-color: ${(props) => props.theme.colors?.border ?? '#E5E7EB'};
  }

  &:focus {
    outline: none;
  }
`;
